/**
 * Risk Assessment Overview — Phase 4.
 *
 * Lists every RA the caller has visibility on (own RAs for regular users;
 * everything in scope for admin/buyer/supervisor — see assertRaCanView server
 * side). Any authenticated user sees a "+ New RA" button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { useDebouncedValue, useLatestRequest } from '../hooks/useSearchRequest';
import { BTN, BTN_XS } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import RaCloneModal from '../components/RaCloneModal';
import RaDeleteConfirmModal from '../components/RaDeleteConfirmModal';
import { formatDateTime } from '../utils/dateFormat';

const STATUS_FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'draft',      label: 'Drafts' },
  { key: 'signed_off', label: 'Signed off' },
];

export default function RiskAssessmentOverview() {
  const navigate = useNavigate();
  const { isAdmin, capabilities } = useDepartment();
  const { user } = useAuth();
  // Site admin cannot create RAs directly — they are not in any production
  // or department, so the resulting RA would have no owner-with-real-dept.
  // To create on behalf of a user, admin must impersonate that user first.
  // Matches the server gate at server/routes/riskAssessments.js
  // requireCanCreateRA.
  const canCreate = !!user && !isAdmin;
  // PER-PRODUCTION library authoring (writes to ra_blocks / ra_templates):
  // canAuthorRiskAssessments in the ACTIVE department (server gate
  // requireDepartmentLibraryAuthor; tranche 3 increment 5 — was the unscoped
  // `isBuyer || activeRole === 'supervisor'`). Admin-only PLATFORM-WIDE library
  // authoring (seed_* tables) is the separate "+ New CrewForge template/block"
  // path on the Library page.
  const canAuthor = isAdmin || capabilities.canAuthorRiskAssessments === true;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem('sfx_ra_overview_status') || 'all');
  const [search, setSearch] = useState('');
  const [cloneTarget, setCloneTarget] = useState(null); // { ra } | null
  const [busyClone, setBusyClone] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { ra, versionCount } | null
  const [busyDelete, setBusyDelete] = useState(false);

  const handleStatusChange = (s) => {
    setStatusFilter(s);
    localStorage.setItem('sfx_ra_overview_status', s);
  };

  // The search box drives a SERVER query, so it needs both halves of the shared
  // hook: debounce it (this page previously issued one request per keystroke,
  // with a full-page spinner each time) and discard stale answers (two requests
  // overlap whenever typing pauses, and the older one can land last).
  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();

  const fetchList = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const qs = params.toString();
      const res = await apiGet(`/api/risk-assessments${qs ? `?${qs}` : ''}`);
      if (!res.ok) throw new Error('Failed to load risk assessments');
      const data = await res.json();
      if (!isCurrent()) return;
      setItems(data);
    } catch (err) {
      // A stale REJECTION is as damaging as a stale success — it paints an
      // error banner over results that loaded perfectly well.
      if (!isCurrent()) return;
      setError(err.message);
    }
    if (isCurrent()) setLoading(false);
  }, [statusFilter, debouncedSearch, beginRequest]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Stale-response guard for the chain-length lookup, independent of the list's.
  // Retrying while the first attempt is still open leaves two requests racing,
  // and the loser is routinely the slower FAILURE — which would stamp "unknown"
  // over a count the retry had just resolved.
  const beginCountRequest = useLatestRequest();

  /**
   * Resolves the version-chain length for the delete modal.
   *
   * 🔑 Answers `null` — never 1 — when it could not find out. A REJECTED fetch,
   * a non-ok response (500/401/403) and an unexpected payload shape are equally
   * uninformative about how long the chain is, so all three take this path.
   *
   * 🚨 It USED to fall through with the literal `versionCount = 1`, described in
   * its own comment as "optimistic". It was not optimistic, it was wrong in the
   * one direction that matters: `RaDeleteConfirmModal` renders "and all N
   * versions in its chain" only when the count exceeds 1, so a failed lookup
   * silently DELETED that clause — while `handleDeleteConfirm` went on calling
   * `DELETE /api/risk-assessments/:id/chain`, which destroys every version
   * regardless. The user typed DELETE having been told less than what happens.
   */
  const fetchVersionCount = useCallback(async (raId) => {
    try {
      const res = await apiGet(`/api/risk-assessments/${raId}`);
      if (!res.ok) return null;
      const data = await res.json();
      if (!Array.isArray(data?.version_chain)) return null;
      // A chain always contains at least the RA itself, so an empty array is a
      // shape we do not understand rather than a count of zero.
      return data.version_chain.length || null;
    } catch {
      return null;
    }
  }, []);

  const loadVersionCount = useCallback(async (raId) => {
    const isCurrent = beginCountRequest();
    setDeleteTarget((prev) => (prev?.ra?.id === raId ? { ...prev, counting: true } : prev));
    const versionCount = await fetchVersionCount(raId);
    if (!isCurrent()) return;
    setDeleteTarget((prev) => (
      prev?.ra?.id === raId ? { ...prev, versionCount, counting: false } : prev
    ));
  }, [beginCountRequest, fetchVersionCount]);

  /**
   * Opens the modal IMMEDIATELY in the counting state, then resolves the count.
   *
   * 🔑 WHY THE DELETE IS NOT BLOCKED WHILE THE COUNT IS UNKNOWN. The honest
   * options were (a) say the count is unknown and state plainly that the whole
   * chain goes, or (b) refuse to proceed until the count is known. (a) was
   * chosen because it fixes the actual defect and (b) does not: the consent
   * problem is that the user was told a SMALLER blast radius than the one they
   * got, and "the whole chain, count unknown" states the true radius exactly —
   * the number was only ever a description of it, since the endpoint deletes
   * every version either way and reads no count from us. (b) adds nothing to
   * what the user is told; it only defers, and it strands a legitimate deletion
   * of a regulated document behind a transient outage — pressure to work around
   * the modal, on the one action that must never be worked around. The Retry is
   * there for anyone who wants the number before committing.
   */
  const handleDeleteRequest = (ra) => {
    setDeleteTarget({ ra, versionCount: null, counting: true });
    loadVersionCount(ra.id);
  };

  const handleDeleteCountRetry = () => {
    if (deleteTarget?.ra) loadVersionCount(deleteTarget.ra.id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.ra) return;
    setBusyDelete(true);
    setError(null);
    try {
      const res = await apiDelete(`/api/risk-assessments/${deleteTarget.ra.id}/chain`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to delete RA');
      }
      setDeleteTarget(null);
      await fetchList();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyDelete(false);
    }
  };

  const handleCloneConfirm = async (newTitle) => {
    if (!cloneTarget?.ra) return;
    setBusyClone(true);
    setError(null);
    try {
      const res = await apiPost(`/api/risk-assessments/${cloneTarget.ra.id}/clone`, { title: newTitle });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Failed to clone RA');
      setCloneTarget(null);
      navigate(`/risk-assessments/${data.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyClone(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-4 max-w-[96rem] mx-auto">
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Risk Assessments</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            All RAs in your production. Click one to view or edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/risk-assessments/library" className={BTN_XS.secondary}>Library</Link>
          {canCreate && (
            <button
              onClick={() => navigate('/risk-assessments/new')}
              className={BTN.primary}
              data-testid="ra-overview-new"
            >
              + New RA
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-md shadow-sm overflow-hidden border border-gray-300 dark:border-gray-600">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => handleStatusChange(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              data-testid={`ra-overview-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, reference, location…"
          className="flex-1 min-w-[200px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          data-testid="ra-overview-search"
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage error={error} />}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No risk assessments yet.
          {canCreate && ' Click "+ New RA" to create one.'}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="space-y-2">
          {items.map((ra) => (
            <div
              key={ra.id}
              onClick={() => navigate(`/risk-assessments/${ra.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/risk-assessments/${ra.id}`);
                }
              }}
              className="block rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid={`ra-overview-item-${ra.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{ra.title}</span>
                    {ra.reference && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">[{ra.reference}]</span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500">v{ra.version}</span>
                    {ra.status === 'signed_off' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <span aria-hidden>🔒</span> Signed off
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                    {ra.location && <span>{ra.location}</span>}
                    {ra.created_by_name && <span>· {ra.created_by_name}</span>}
                    <span>· Created {formatDateTime(ra.created_at)}</span>
                    {ra.signature_count > 0 && (
                      <span>· {ra.signature_count} signature{ra.signature_count > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/risk-assessments/${ra.id}`);
                    }}
                    className={BTN_XS.secondary}
                    data-testid={`ra-overview-open-${ra.id}`}
                  >
                    Open
                  </button>
                  {canCreate && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCloneTarget({ ra });
                      }}
                      className={BTN_XS.secondary}
                      data-testid={`ra-overview-clone-${ra.id}`}
                    >
                      Clone as new RA
                    </button>
                  )}
                  {/* The list is ringfenced to the ACTIVE department, so the
                      active-department capability is the RA's-own-department
                      answer for every row (server editor gate resolves
                      ra.department_id; here they are equal). */}
                  {(isAdmin || capabilities.canAuthorRiskAssessments === true || (user && ra.created_by === user.id)) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRequest(ra);
                      }}
                      className={BTN_XS['danger-soft']}
                      data-testid={`ra-overview-delete-${ra.id}`}
                      title="Delete this RA and all its versions"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <RaCloneModal
        open={!!cloneTarget}
        title="Clone as new RA"
        label="New RA title"
        defaultValue={cloneTarget?.ra ? `${cloneTarget.ra.title} — Copy` : ''}
        confirmLabel="Clone"
        busy={busyClone}
        onCancel={() => { if (!busyClone) setCloneTarget(null); }}
        onConfirm={handleCloneConfirm}
      />

      {/* 🚨 NEVER `?? 1` / `|| 1` on versionCount — a fallback count IS the
          defect, not a guard against it. `null` means unknown and the modal
          says so; `1` silently deletes the "and all N versions" clause. */}
      <RaDeleteConfirmModal
        open={!!deleteTarget}
        raTitle={deleteTarget?.ra?.title || ''}
        versionCount={deleteTarget?.versionCount ?? null}
        counting={deleteTarget?.counting === true}
        onRetryCount={handleDeleteCountRetry}
        busy={busyDelete}
        onCancel={() => { if (!busyDelete) setDeleteTarget(null); }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
